import React from 'react'
import { match } from 'ts-pattern'

import { SurveyGroupCustomerAttributeMultiSelector } from '~/components/features/SurveyGroupCustomerAttributeMultiSelector'
import {
  SurveyGroupCustomerAttributeSelector,
  type SurveyGroupCustomerAttributeSelectorProps,
} from '~/components/features/SurveyGroupCustomerAttributeSelector'
import {
  type ConditionOperation,
  type SurveyGroupCustomerAttribute as SurveyGroupCustomerAttributeBase,
} from '~/gql/graphql'
import { unassignedSymbol } from '~/utils'

type SurveyGroupCustomerAttribute = App.PickGraphQL<
  SurveyGroupCustomerAttributeBase,
  'name'
> & {
  idLike: string
}

export type SurveyGroupCustomerAttributeConditionalSelectorProps<
  TSurveyGroupCustomerAttribute extends SurveyGroupCustomerAttribute,
> = {
  className?: string
  conditionOperation: ConditionOperation
  customerAttributes: TSurveyGroupCustomerAttribute[]
  disabled?: boolean
  onBlur?: () => void
  onChange?: (
    customerAttributeIdLikes: TSurveyGroupCustomerAttribute['idLike'][],
  ) => void
  value: TSurveyGroupCustomerAttribute['idLike'][]
}

export const SurveyGroupCustomerAttributeConditionalSelector = React.forwardRef(
  <TSurveyGroupCustomerAttribute extends SurveyGroupCustomerAttribute>(
    {
      className,
      conditionOperation,
      customerAttributes,
      disabled,
      onBlur,
      onChange,
      value,
    }: SurveyGroupCustomerAttributeConditionalSelectorProps<TSurveyGroupCustomerAttribute>,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const handleCustomerAttributeChange = React.useCallback<
      EH<
        SurveyGroupCustomerAttributeSelectorProps<TSurveyGroupCustomerAttribute>,
        'onChange'
      >
    >(
      (customerAttributeIdLike) => {
        if (customerAttributeIdLike !== unassignedSymbol) {
          onChange?.([customerAttributeIdLike])
        }
      },
      [onChange],
    )

    return (
      <>
        {match(conditionOperation)
          .with('equal', 'not_equal', () => (
            <SurveyGroupCustomerAttributeSelector
              ref={ref}
              className={className}
              customerAttributes={customerAttributes}
              disabled={disabled}
              value={value[0]}
              onBlur={onBlur}
              onChange={handleCustomerAttributeChange}
            />
          ))
          .with('in', 'not_in', () => (
            <SurveyGroupCustomerAttributeMultiSelector
              ref={ref}
              className={className}
              customerAttributes={customerAttributes}
              disabled={disabled}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
            />
          ))
          .exhaustive()}
      </>
    )
  },
)
